/**
 * Logging / telemetry guards for anonymous learning analytics.
 *
 * - Never log aggregate request bodies or score payloads
 * - Never put schoolPublicId in URL query strings
 * - Redact assessment answer / score keys from Sentry breadcrumbs
 */

/** Keys that must never appear in logs, Sentry extras, or analytics events. */
export const ASSESSMENT_TELEMETRY_BLOCKED_KEYS = [
  'rawAnswer',
  'rawAnswers',
  'answers',
  'questionId',
  'baseline',
  'final',
  'learningDelta',
  'domainScores',
  'domainBaseline',
  'domainFinal',
  'exactScore',
  'contribution',
  'aggregate',
  'domainBaselineBands',
  'domainFinalBands',
  'domainDeltaBands',
  'LocalAssessmentRecord',
] as const;

const BLOCKED_SET = new Set(
  ASSESSMENT_TELEMETRY_BLOCKED_KEYS.map((k) => k.toLowerCase()),
);

export function isAssessmentTelemetryBlockedKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (BLOCKED_SET.has(lower)) return true;
  return (
    lower.includes('rawanswer') ||
    lower.includes('domainscore') ||
    lower.includes('learningdelta') ||
    lower.includes('exactscore')
  );
}

/**
 * Strip assessment/result payloads from an object before logging or Sentry.
 * Replaces blocked values with '[AssessmentRedacted]'.
 */
export function redactAssessmentTelemetry<T>(value: T, depth = 0): T {
  if (depth > 8 || value == null) return value;
  if (Array.isArray(value)) {
    return value.map((item) => redactAssessmentTelemetry(item, depth + 1)) as T;
  }
  if (typeof value !== 'object') return value;

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (isAssessmentTelemetryBlockedKey(key)) {
      out[key] = '[AssessmentRedacted]';
    } else {
      out[key] = redactAssessmentTelemetry(child, depth + 1);
    }
  }
  return out as T;
}

/**
 * Safe log line for the aggregate endpoint — metadata only, never body.
 */
export function safeAggregateEndpointLogMeta(meta: {
  method: string;
  status: number;
  schoolPublicIdPresent: boolean;
  cohortSizeBand?: string;
  duplicate?: boolean;
}): Record<string, unknown> {
  return {
    event: 'anonymous_aggregate_submit',
    method: meta.method,
    status: meta.status,
    schoolPublicIdPresent: meta.schoolPublicIdPresent,
    cohortSizeBand: meta.cohortSizeBand ?? null,
    duplicate: meta.duplicate ?? false,
    // Intentionally omit: body, scores, schoolPublicId value, payloadHash contents
  };
}

/** Routes where session replay / fine-grained analytics must not capture answers. */
export const ANONYMOUS_ASSESSMENT_ROUTE_PATTERNS = [
  /^\/aloitus/i,
  /^\/loppuhaaste/i,
  /^\/module\/.*\/test/i,
  /^\/demo\//i,
] as const;

export function isAnonymousAssessmentRoute(pathname: string): boolean {
  return ANONYMOUS_ASSESSMENT_ROUTE_PATTERNS.some((re) => re.test(pathname));
}
